import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box, Stack } from '@dropbox/dig-foundations';
import { Stepper } from '@dropbox/dig-components/stepper';
import { useSiteCode } from 'js/sign-components/common/use-site-code';
import { NotificationBanners } from 'signer-app/parts/notifications/notification-banners';
import { urlHelper } from 'signer-app/utils/url-helpers';
import Logo from 'signer-app/parts/logo-v2';
import { CountryCodeProvider } from 'signer-app/authentication/use-country-code';
import './layout.scss';
import { useBreakpoint } from '@dropbox/dig-components/hooks';

interface Props {
  screenLeftSide: React.ReactElement;
  screenRightSide: React.ReactElement;
  currentIndex?: number;
  dataTestId?: string;
}

const messages = defineMessages({
  stepAccount: {
    id: '',
    defaultMessage: 'Account',
    description:
      'stepper, step, informs user that current step is for account activities',
  },
  stepPayment: {
    id: '',
    defaultMessage: 'Payment',
    description:
      'stepper, step, informs user that current step is for payments activities',
  },
  localizedStepperInfo: {
    id: '',
    defaultMessage: 'Step {stepNumberInt} of {stepsTotalNumberInt}',
    description:
      'accessibility text for stepper, informs user of current step in progressive checkout stage. Steps are numbered here instead of text',
  },
});

const ProgressiveCheckoutLayout: React.FunctionComponent<Props> = ({
  screenLeftSide,
  screenRightSide,
  currentIndex = 0,
  dataTestId = 'progressive-layout',
}: Props) => {
  const intl = useIntl();
  const { xsmall: displayXSmall, medium: displayMedium } = useBreakpoint();

  const siteCode = useSiteCode();
  const logoHref = urlHelper('info/pricing', siteCode);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      data-testid={dataTestId}
    >
      <CountryCodeProvider>
        <NotificationBanners />
        {/* Row */}
        <Box
          display="flex"
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
          flexDirection={{
            xsmall: 'column',
            medium: 'row',
          }}
        >
          {/* Left panel */}
          <Box
            display="flex"
            flexGrow={{
              xsmall: 0,
              medium: 1,
            }}
            flexShrink={{
              xsmall: 0,
              medium: 1,
            }}
            flexBasis={{
              xsmall: 'auto',
              medium: 1,
            }}
          >
            <Stack
              nestedWrapperHtmlAttributes={{
                style: {
                  width: '100%',
                },
              }}
              display="flex"
            >
              <Box>
                <Logo
                  size={40}
                  siteCode={siteCode}
                  href={logoHref}
                  // className={styles.logo}
                  variant="wordmark"
                />
              </Box>
              <Box>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="center"
                  paddingY="Macro Large"
                  margin={{ medium: 'auto' }}
                  className="progressiveCheckoutBoxWidth"
                >
                  <Box width={{ medium: '100%' }}>
                    <Stack paddingX={{ xsmall: 'Macro XSmall', medium: '0' }}>
                      <Box
                        paddingBottom={{ xsmall: 'Macro Large', medium: '68' }}
                      >
                        <Stepper
                          currentIndex={currentIndex}
                          steps={[
                            {
                              label: intl.formatMessage(messages.stepAccount),
                            },
                            {
                              label: intl.formatMessage(messages.stepPayment),
                            },
                          ]}
                          localizedSummary={(current: number, total: number) =>
                            intl.formatMessage(messages.localizedStepperInfo, {
                              stepNumberInt: current,
                              stepsTotalNumberInt: total,
                            })
                          }
                        />
                      </Box>
                      <Box>{screenLeftSide}</Box>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Box>
          {/* Right panel */}
          <Box
            display="flex"
            flexGrow={{
              xsmall: 0,
              medium: 1,
            }}
            flexShrink={{
              xsmall: 0,
              medium: 1,
            }}
            flexBasis={{
              xsmall: 'auto',
              medium: 1,
            }}
            alignItems="flex-start"
            justifyContent="center"
            backgroundColor="Background Subtle"
          >
            <Box
              paddingX={{ xsmall: 'Macro XSmall', medium: '84' }}
              paddingY={{ xsmall: '44', medium: 'Macro Large' }}
              marginTop={{ medium: '132' }}
              display="flex"
              flexBasis={{ small: 'auto', medium: 0 }}
              style={{
                flex:
                  displayMedium && displayXSmall ? '0.75 0.75 0' : '1 1 auto',
              }}
            >
              {screenRightSide}
            </Box>
          </Box>
        </Box>
      </CountryCodeProvider>
    </Box>
  );
};

export default ProgressiveCheckoutLayout;
